import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../components/forms/input.component';
import Select from 'react-select';
import Button from '../../components/forms/button.component';
import Title from '../../components/forms/titles.component';
import SimpleTitle from '../../components/forms/simple-titles.component';
import AdvancedCheckbox from '../../components/forms/advanced-checkbox.component';
import useStepper from '../../hooks/use-stepper.hook';
import InvalidFeedback from '../../components/forms/invalid-feedback.component';
import Icon from '../../components/forms/icon.component';
import useInputValidation from '../../hooks/use-input-validation.hook';
import { AlertService } from '../../services/alert.service';
import { CompaniesService } from '../../services/companies.service';
import { UsersService } from '../../services/users.service';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import AddCompanyModal from './modal/add-company.modal';
import { AccountsService } from '../../services/accounts.service';
import LogData from '../../components/logs/log-data.component';
import { ChannelsService } from '../../services/channels.service';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Navigate from '../../components/cards/header-navigate.component';
import Header from '../../components/layout/header.component';
import Asaid from '../../components/layout/asaid.component';
import Footer from '../../components/layout/footer.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import InputCopyClipboard from '../../components/forms/clipboard-copy.component';

interface AccountStepperProps {
  onCancel: () => void;
  Companies: any;
  onSucess: () => void;
}

interface Channel {
  _id: string;
  price: string;
}

interface FormValues {
  name: string;
  company: string;
  admin_id?: string;
  admin?: UserFormValues;
  channels: {
    whatsapp: Channel[];
    rcs_basic: Channel[];
    rcs_single: Channel[];
    rcs_conversational: Channel[];
    sms: Channel[];
    email: Channel[];
  };
}

interface UserFormValues {
  username: string;
  full_name: string;
  email: string;
}

interface UserOption {
  value: string;
  label: string;
  userData: UserFormValues;
}

interface ChannelData {
  value: string;
  label: string;
}

function AccountStepper({ Companies, onSucess }: AccountStepperProps) {
  useBgPages();
  const { t } = useTranslation();
  const { currentStep, setCurrentStep, resteStepper } = useStepper('add_new_area_stepper');
  const { priceRegex, handlePrice, emailRegex } = useInputValidation();
  const ReactSwal = withReactContent(Swal);

  const defaultValues: FormValues = {
    name: '',
    company: '',
    admin_id: '',
    admin: {
      username: '',
      full_name: '',
      email: ''
    },
    channels: {
      whatsapp: [{ _id: '', price: '' }],
      rcs_conversational: [{ _id: '', price: '' }],
      rcs_single: [{ _id: '', price: '' }],
      rcs_basic: [{ _id: '', price: '' }],
      sms: [{ _id: '', price: '' }],
      email: [{ _id: '', price: '' }]
    }
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const {
    fields: whatsappFields,
    append: whatsappAppend,
    remove: whatsappRemove
  } = useFieldArray({
    control,
    name: 'channels.whatsapp'
  });

  const {
    fields: rcsSingleFields,
    append: rcsSingleAppend,
    remove: rcsSingleRemove
  } = useFieldArray({
    control,
    name: 'channels.rcs_single'
  });

  const {
    fields: rcsBasicFields,
    append: rcsBasicAppend,
    remove: rcsBasicRemove
  } = useFieldArray({
    control,
    name: 'channels.rcs_basic'
  });

  const {
    fields: rcsConversationalFields,
    append: rcsConversationalAppend,
    remove: rcsConversationalRemove
  } = useFieldArray({
    control,
    name: 'channels.rcs_conversational'
  });

  const {
    fields: smsFields,
    append: smsAppend,
    remove: smsRemove
  } = useFieldArray({
    control,
    name: 'channels.sms'
  });

  const {
    fields: emailFields,
    append: emailAppend,
    remove: emailRemove
  } = useFieldArray({
    control,
    name: 'channels.email'
  });

  const [channelConfig, setChannelConfig] = useState({
    whatsapp: false,
    rcs_conversational: false,
    rcs_single: false,
    rcs_basic: false,
    sms: false,
    email: false
  });

  const handleCheckboxChange = (channelName: keyof typeof channelConfig) => {
    setChannelConfig((prevConfig) => {
      const newValue = !prevConfig[channelName];

      if (!newValue) {
        setValue(`channels.${channelName}`, [
          {
            _id: '',
            price: ''
          }
        ]);
      }

      return {
        ...prevConfig,
        [channelName]: newValue
      };
    });
  };

  const [companies, setCompanies] = useState(Companies);
  const [userCompanies, setUserCompanies] = useState<UserOption[]>([]);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [listData, setListaData] = useState({
    channelWhatsApp: [] as ChannelData[],
    channelRcsConversacional: [] as ChannelData[],
    channelRcsSingle: [] as ChannelData[],
    channelRcsBasic: [] as ChannelData[],
    channelSMS: [] as ChannelData[],
    channelEmail: [] as ChannelData[]
  });

  const [showUserForm, setShowUserForm] = useState(false);
  const [isUserCreated, setIsUserCreated] = useState(false);

  const companySelected = watch('company');
  const userSelected = watch('admin_id');

  const validateStep = (step: number, values: FormValues): boolean => {
    switch (step) {
      case 1: {
        const accountNameValid = !!values.name?.trim();
        const companyValid = !!values.company;
        return accountNameValid && companyValid;
      }
      case 2: {
        if (!!values.admin_id) {
          return true;
        }
        return false;
      }
      case 3: {
        const { whatsapp, rcs_basic, rcs_single, rcs_conversational, sms, email } = values.channels;

        const validateChannelArray = (channels: Channel[]): boolean => {
          for (const channel of channels) {
            // Si se ingresa uno pero no el otro, lanza la advertencia
            if ((channel._id && !channel.price) || (!channel._id && channel.price)) {
              return false;
            }
          }
          return true;
        };

        return (
          validateChannelArray(whatsapp) &&
          validateChannelArray(rcs_basic) &&
          validateChannelArray(rcs_single) &&
          validateChannelArray(rcs_conversational) &&
          validateChannelArray(sms) &&
          validateChannelArray(email)
        );
      }
      default:
        return false;
    }
  };

  const isCurrentStepValid = validateStep(currentStep, watch());

  const getWaAccounts = async () => {
    if (!companySelected) return;
    try {
      const response = await AccountsService.getWaAccountsForCompany(companySelected);
      const activeAccounts = response.filter((item: any) => item.status === true);
      const formattedData = activeAccounts.map((item: any) => ({
        value: item._id,
        label: item.name
      }));
      setListaData((prev) => ({
        ...prev,
        channelWhatsApp: formattedData
      }));
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  };

  const getAgentsRCS = async () => {
    if (!companySelected) return;
    try {
      const response = await AccountsService.getRCSAccountsForCompany(companySelected);

      setListaData((prev) => ({
        ...prev,
        channelRcsConversacional: response
          .filter((item: any) => item.type.toUpperCase() === 'CONVERSATIONAL')
          .map((item: any) => ({ value: item._id, label: item.name })),
        channelRcsSingle: response
          .filter((item: any) => item.type.toUpperCase() === 'SINGLE')
          .map((item: any) => ({ value: item._id, label: item.name })),
        channelRcsBasic: response
          .filter((item: any) => item.type.toUpperCase() === 'BASIC')
          .map((item: any) => ({ value: item._id, label: item.name }))
      }));
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  };

  const getOutputsSMS = async () => {
    try {
      const response = await ChannelsService.getMasksSMS();
      const activeAccounts = response.filter((item: any) => item.status === true);
      const formattedData = activeAccounts.map((item: any) => ({
        value: item._id,
        label: item.name
      }));

      setListaData((prev) => ({
        ...prev,
        channelSMS: formattedData
      }));
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  };

  const getEmailAccounts = async () => {
    try {
      const response = await ChannelsService.getMailingAccounts();
      const formattedData = response.map((item: any) => ({
        value: item._id.toString(),
        label: item.name
      }));

      setListaData((prev) => ({
        ...prev,
        channelEmail: formattedData
      }));
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  };

  const onAddNewCompany = () => {
    setIsAddCompanyModalOpen(true);
  };

  const handleAddCompany = async (companyName: string) => {
    if (!companyName.trim()) {
      AlertService.toastWarning(t('account-page.modal-add-account.error-company-name-required'));
      return;
    }

    try {
      const newCompany = await CompaniesService.saveCompany({
        name: companyName
      });
      setCompanies((prev: any) => [...prev, { value: newCompany._id, label: companyName }]);
      setValue('company', newCompany._id);
      AlertService.toastSuccess(t('account-page.modal-add-account.success-company-added'));
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const handleSaveUser = () => {
    const username = watch('admin.username').trim();
    const full_name = watch('admin.full_name').trim();
    const email = watch('admin.email').trim();

    if (!username || !full_name || !email) {
      AlertService.toastWarning(t('account-page.modal-add-account.error-missing-fields'));
      return;
    }

    const newUser: UserOption = {
      value: username.toLowerCase().replace(/\s/g, '_'),
      label: full_name,
      userData: { username, full_name, email }
    };

    setUserCompanies((prev) => [...prev, newUser]);
    setValue('admin_id', newUser.value);
    setIsUserCreated(true);
    setShowUserForm(false);
    AlertService.toastSuccess(t('account-page.modal-add-account.success-user-added'));
  };

  const generateRecapHTML = (data: FormValues) => {
    const { name, company, admin, channels } = data;
    const companyLabel = companies.find((c: any) => c.value === company)?.label || company;
    const adminName = admin?.full_name ?? userSelected;

    let html = `
      <div class="mb-8">
        ${t('account-page.modal-add-account.confirm-message')}
      </div>
      <div class="d-flex align-items-center flex-column mb-5">
        <span class="badge badge-light-primary fs-6 mb-2">
          <i class="ki-duotone ki-briefcase fs-2x text-primary me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
                <span class="path4"></span>
          </i>
          ${name}
        </span>
          <li class="mb-3 d-flex align-items-center justify-">
            <i class="ki-duotone ki-briefcase fs-2x text-primary me-3">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            <span class="fw-bold me-2">${t('account-page.modal-add-account.label-company')}:</span>
            <span>${companyLabel}</span>
          </li>
          <li class="mb-3 d-flex align-items-center">
            <i class="ki-duotone ki-user fs-2x text-primary me-3">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            <span class="fw-bold me-2">${t('account-page.modal-add-account.label-user')}:</span>
            <span>${adminName}</span>
          </li>
      </div>
    `;

    const renderChannels = (
      title: string,
      iconClass: string,
      colorClass: string,
      list: any[],
      listOptions: any[],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      channelKey: string
    ) => {
      if (!list || list.length === 0) return '';
      return `
        <div class="d-flex align-items-center ms-10">
          <i class="${iconClass} fs-2x text-${colorClass} me-3">
                 <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
                <span class="path4"></span>
          </i>
          <span class="fw-bold me-2">${title}:</span>
        </div>
        <ul class="list-unstyled ms-15 pe-10">
          ${list
            .map((channel) => {
              const label =
                listOptions.find((option) => option.value === channel._id)?.label || channel._id;
              return `
                <li class="d-flex justify-content-between align-items-center mt-3">
                  <div class="d-flex flex-column text-start">
                    <span class="text-muted">${t('roles.account-label')}</span>
                    <span class="fw-bold text-gray-900">${label}</span>
                  </div>
                  <div class="d-flex flex-column text-end">
                    <span class="text-muted">${t('account-page.price')}</span>
                    <span class="fw-bold text-gray-900">$${channel.price}</span>
                  </div>
                </li>
              `;
            })
            .join('')}
        </ul>
      `;
    };

    html += renderChannels(
      'WhatsApp',
      'ki-duotone ki-whatsapp',
      'primary',
      channels.whatsapp,
      listData.channelWhatsApp,
      'whatsapp'
    );

    html += renderChannels(
      'RCS Conversational',
      'ki-duotone ki-messages',
      'info',
      channels.rcs_conversational,
      listData.channelRcsConversacional,
      'rcsConversational'
    );

    html += renderChannels(
      'RCS Single',
      'ki-duotone ki-messages',
      'info',
      channels.rcs_single,
      listData.channelRcsSingle,
      'rcsSingle'
    );
    html += renderChannels(
      'RCS Basic',
      'ki-duotone ki-messages',
      'info',
      channels.rcs_basic,
      listData.channelRcsBasic,
      'rcsBasic'
    );

    html += renderChannels(
      t('account-page.modal-add-account.channels.sms.outputLabel'),
      'ki-duotone ki-message-notif',
      'warning',
      channels.sms,
      listData.channelSMS,
      'sms'
    );

    html += renderChannels(
      t('account-page.modal-add-account.channels.email.outputLabel'),
      'ki-duotone ki-message-text-2',
      'primary',
      channels.email,
      listData.channelEmail,
      'email'
    );

    return html;
  };

  const onSubmit = async (data: FormValues) => {
    if (!validateStep(1, data) || !validateStep(2, data) || !validateStep(3, data)) {
      AlertService.toastError(t('account-page.modal-add-account.error-incomplete'));
      return;
    }

    const cleanedChannels: FormValues['channels'] = {
      whatsapp: data.channels.whatsapp.filter((channel) => channel._id && channel.price),
      rcs_conversational: data.channels.rcs_conversational.filter(
        (channel) => channel._id && channel.price
      ),
      rcs_single: data.channels.rcs_single.filter((channel) => channel._id && channel.price),
      rcs_basic: data.channels.rcs_basic.filter((channel) => channel._id && channel.price),
      sms: data.channels.sms.filter((channel) => channel._id && channel.price),
      email: data.channels.email.filter((channel) => channel._id && channel.price)
    };

    const recapHTML = generateRecapHTML({ ...data, channels: cleanedChannels });
    const buttonId = `addAccount-${data.name}`;

    await ReactSwal.fire({
      title: `<h2>${t('account-page.modal-add-account.confirm-title')}</h2>`,
      html: recapHTML,
      icon: 'warning',
      iconColor: '#FFC700',
      width: '500px',
      showCancelButton: true,
      customClass: {
        cancelButton: 'btn btn-secondary order-1  me-3',
        confirmButton: 'btn btn-primary order-2'
      },
      confirmButtonText: t('general.continue'),
      cancelButtonText: t('general.btn-cancel'),
      buttonsStyling: false,
      showConfirmButton: true,
      focusDeny: true,
      focusCancel: true,
      showLoaderOnConfirm: true,
      focusConfirm: false
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let formattedData: any = {
            company: data.company,
            name: data.name,
            ...(isUserCreated
              ? {
                  admin: {
                    username: data.admin?.username?.trim().replace(/\s/g, '_'),
                    full_name: data.admin?.full_name,
                    email: data.admin?.email
                  }
                }
              : { admin_id: data.admin_id })
          };

          const hasChannels = Object.values(cleanedChannels).some(
            (channelList) => channelList.length > 0
          );

          if (hasChannels) {
            formattedData = {
              ...formattedData,
              channels: Object.entries(cleanedChannels).reduce(
                (acc, [key, value]) => {
                  if (value.length > 0) {
                    acc[key] = value.map((channel) => ({
                      config: channel._id,
                      price: parseFloat(channel.price)
                    }));
                  }
                  return acc;
                },
                {} as Record<string, { config: string; price: number }[]>
              )
            };
          }

          const response = await AccountsService.saveAccount(formattedData);

          if (response.adminPassword) {
            const html = (
              <div>
                <p>Esta es la contraseña del usuario que creaste: </p>
                <div className="width-fit-content m-auto">
                  <InputCopyClipboard text={response.adminPassword} />
                </div>
              </div>
            );

            await AlertService.success(<p className="h2">¡ Contraseña de usuario !</p>, html);
          } else {
            AlertService.toastSuccess(t('account-page.modal-add-account.success-message'));
          }
          LogData(formattedData, buttonId, window.location.href);
          onSucess();
          resetForm();
        } catch (errorCode: any) {
          console.log('Error: ', errorCode);
          AlertService.toastError(errorCode.error || errorCode);
        }
      }
    });
  };

  const resetForm = () => {
    reset(defaultValues);
    resteStepper.goFirst();
    setCurrentStep(1);
  };

  const resetUserData = () => {
    setValue('admin.username', '');
    setValue('admin.full_name', '');
    setValue('admin.email', '');
  };

  const handleShowUserForm = () => {
    setShowUserForm(true);
    setValue('admin_id', '');
  };

  useEffect(() => {
    setCompanies(Companies);
  }, [Companies]);

  useEffect(() => {
    if (companySelected) {
      const fetchUsers = async () => {
        try {
          const data: any = await UsersService.getAdminsForCompany(companySelected);
          const usersOptions = data.map((user: any) => ({
            value: user._id,
            label: user.full_name,
            userData: user
          }));
          setUserCompanies(usersOptions);
        } catch (errorCode: any) {
          console.log('Error: ', errorCode);
          AlertService.toastError(errorCode.error || errorCode);
        }
      };
      fetchUsers();
    } else {
      setUserCompanies([]);
    }
  }, [companySelected]);

  useEffect(() => {
    if (!showUserForm) {
      if (userSelected) {
        const selectedUser = userCompanies.find((user) => user.value === userSelected);
        if (selectedUser) {
          setValue('admin.username', selectedUser.userData.username);
          setValue('admin.full_name', selectedUser.label);
          setValue('admin.email', selectedUser.userData.email);
        }
      } else {
        resetUserData();
      }
    }
    if (showUserForm) {
      resetUserData();
    }
  }, [userSelected, showUserForm]);

  useEffect(() => {
    if (companySelected) {
      getWaAccounts();
      getAgentsRCS();
      getOutputsSMS();
      getEmailAccounts();
    }
  }, [companySelected]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex min-vh-100 flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
          <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
            <div className="container-xxl">
              <Navigate
                title={'Registrar'}
                titleBolder={'cuenta'}
                subtitle={'Completa los pasos para poder crear una cuenta.'}
                breadcrumbItems={['Operaciones', 'Registro']}
                btnBack
              />

              <form className="form" onSubmit={handleSubmit(onSubmit)}>
                <div className="row gy-5 g-xl-10 stepper" id="add_new_area_stepper">
                  <div className="col-xl-4 mb-xl-10">
                    <div className="card mb-5 mb-xxl-8">
                      <div className="card-header align-items-center border-0 mt-5">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="fw-bolder fw-bold fs-2">{t('stepper.title')}</span>
                          <span className="text-gray-400 mt-2 fw-semibold fs-6">
                            {t('stepper.subtitle')}
                          </span>
                        </h3>
                      </div>
                      <div className="card-body pt-1">
                        <div className="col-12 mb-10 mb-lg-0 h-100 p-10">
                          <div
                            className="stepper stepper-pills stepper-column d-flex flex-column flex-lg-row"
                            id="add_new_area_stepper"
                          >
                            <div className="stepper-nav">
                              <div
                                className="stepper-item me-5 current"
                                data-kt-stepper-element="nav"
                              >
                                <div className="stepper-wrapper d-flex align-items-center">
                                  <div className="stepper-icon w-40px h-40px">
                                    <i className="stepper-check fas fa-check" />
                                    <span className="stepper-number">1</span>
                                  </div>
                                  <div className="stepper-label">
                                    <h3 className="stepper-title">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-data-account.title'
                                      )}
                                    </h3>
                                    <div className="stepper-desc">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-data-account.description'
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="stepper-line h-40px" />
                              </div>
                              <div className="stepper-item me-5" data-kt-stepper-element="nav">
                                <div className="stepper-wrapper d-flex align-items-center">
                                  <div className="stepper-icon w-40px h-40px">
                                    <i className="stepper-check fas fa-check" />
                                    <span className="stepper-number">2</span>
                                  </div>
                                  <div className="stepper-label">
                                    <h3 className="stepper-title">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-user.title'
                                      )}
                                    </h3>
                                    <div className="stepper-desc">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-user.description'
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="stepper-line h-40px" />
                              </div>
                              <div className="stepper-item me-5" data-kt-stepper-element="nav">
                                <div className="stepper-wrapper d-flex align-items-center">
                                  <div className="stepper-icon w-40px h-40px">
                                    <i className="stepper-check fas fa-check" />
                                    <span className="stepper-number">3</span>
                                  </div>
                                  <div className="stepper-label">
                                    <h3 className="stepper-title">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-channels.title'
                                      )}
                                    </h3>
                                    <div className="stepper-desc">
                                      {t(
                                        'account-page.modal-add-account.modal-steps.step-channels.description'
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between flex-nowrap text-nowrap py-1"></div>
                        <div className="d-flex flex-center flex-row-fluid pt-0 gap-7 px-5">
                          <div className="col-6">
                            <Button
                              type="button"
                              typeStepperBtn="previous"
                              id="previous"
                              className="btn btn-lg btn-secondary w-100 hover-scale"
                            >
                              <Icon icon="ki-double-left" size="fs-1" />
                              {t('general.back')}
                            </Button>
                          </div>
                          <div className="col-6">
                            <Button
                              type="submit"
                              typeStepperBtn="submit"
                              id="sendData"
                              className="btn btn-lg btn-primary w-100 hover-scale"
                              disabled={!isValid || !isCurrentStepValid}
                              isLoading={isSubmitting}
                            >
                              {t('general.save')}{' '}
                              <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                            </Button>
                            <Button
                              type="button"
                              className="btn btn-lg btn-primary w-100 hover-scale text-nowrap"
                              id="next"
                              typeStepperBtn="next"
                              disabled={!isCurrentStepValid}
                            >
                              {t('general.continue')}
                              <Icon icon="ki-double-right" size="fs-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-8 mb-5 mb-xl-10">
                    <div className="card mb-xxl-8">
                      <div className="card-body pt-13">
                        {/* Primer Stepper */}
                        <div className="flex-column current" data-kt-stepper-element="content">
                          <div className="pb-8 pb-lg-10">
                            <Title
                              icon="ki-briefcase"
                              title={t('account-page.modal-add-account.title-company')}
                              subtitle={
                                <span className="d-inline-flex align-items-center align-line-height">
                                  {t('account-page.modal-add-account.subtitle-company')}{' '}
                                  <button
                                    type="button"
                                    className="btn text-primary cursor-pointer px-1 py-0"
                                    onClick={onAddNewCompany}
                                  >
                                    {t('account-page.modal-add-account.click-here-company')}
                                  </button>
                                </span>
                              }
                              type="info"
                            />
                            <div className="fv-row mb-5">
                              <label className="form-label">
                                {t('account-page.modal-add-account.label-companies')}
                                <span className="required"></span>
                              </label>
                              <Controller
                                control={control}
                                name="company"
                                rules={{ required: true }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                  <>
                                    <Select
                                      options={companies}
                                      className={
                                        error
                                          ? 'form-select-custom is-invalid'
                                          : 'form-select-custom'
                                      }
                                      classNamePrefix="form-select-custom"
                                      placeholder={t(
                                        'account-page.modal-add-account.placeholder-select-company'
                                      )}
                                      onChange={(selectedOption) =>
                                        onChange(selectedOption ? selectedOption.value : '')
                                      }
                                      value={
                                        companies?.find((option: any) => option.value === value) ||
                                        null
                                      }
                                      noOptionsMessage={({ inputValue }) =>
                                        inputValue
                                          ? t('general.no-options', { inputValue })
                                          : t('general.no-options')
                                      }
                                    />
                                    {error && <InvalidFeedback error={error} />}
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="pb-8 pb-lg-10">
                            <Title
                              icon="ki-wallet"
                              title={t('account-page.modal-add-account.title-account-name')}
                              subtitle={t('account-page.modal-add-account.subtitle-account-name')}
                            />
                            <div className="fv-row mb-5">
                              <Input
                                label={t('account-page.modal-add-account.label-account-name')}
                                requiredIndicator="required"
                                placeholder={t(
                                  'account-page.modal-add-account.placeholder-account-name'
                                )}
                                errors={errors?.name}
                                {...register('name', {
                                  required: true,
                                  setValueAs: (value) => value.trim()
                                })}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Segundo Stepper */}
                        <div className="flex-column" data-kt-stepper-element="content">
                          <div className="pb-8 pb-lg-10">
                            {!showUserForm ? (
                              <>
                                <Title
                                  type="info"
                                  icon="ki-user"
                                  title={t('account-page.modal-add-account.title-user')}
                                  subtitle={
                                    <>
                                      {t('account-page.modal-add-account.subtitle-user')}{' '}
                                      <span
                                        className="text-primary cursor-pointer"
                                        onClick={handleShowUserForm}
                                      >
                                        {t('account-page.modal-add-account.click-here-user')}
                                      </span>
                                    </>
                                  }
                                />
                                <div className="fv-row mb-5">
                                  <label className="form-label">
                                    {t('account-page.modal-add-account.label-users')}
                                    <span className="required"></span>
                                  </label>
                                  <Controller
                                    control={control}
                                    name="admin_id"
                                    rules={{ required: true }}
                                    render={({
                                      field: { onChange, value },
                                      fieldState: { error }
                                    }) => (
                                      <>
                                        <Select
                                          options={userCompanies}
                                          className={
                                            error
                                              ? 'form-select-custom is-invalid'
                                              : 'form-select-custom'
                                          }
                                          classNamePrefix="form-select-custom"
                                          placeholder={t(
                                            'account-page.modal-add-account.placeholder-select-user'
                                          )}
                                          onChange={(selectedOption) =>
                                            onChange(selectedOption ? selectedOption.value : '')
                                          }
                                          value={
                                            userCompanies.find(
                                              (option) => option.value === value
                                            ) || null
                                          }
                                          noOptionsMessage={({ inputValue }) =>
                                            inputValue
                                              ? t('general.no-options', { inputValue })
                                              : t('general.no-options')
                                          }
                                        />
                                        {error && <InvalidFeedback error={error} />}
                                      </>
                                    )}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className="pb-8 pb-lg-10">
                                <Title
                                  icon="ki-user"
                                  title={t('account-page.modal-add-account.title-add-user')}
                                  subtitle={t('account-page.modal-add-account.subtitle-add-user')}
                                  type="info"
                                />
                                <div className="d-flex flex-column gap-3">
                                  {/* Nombre de Usuario */}
                                  <Input
                                    label={t('account-page.modal-add-account.label-username')}
                                    requiredIndicator="required"
                                    placeholder={t(
                                      'account-page.modal-add-account.placeholder-username'
                                    )}
                                    errors={errors.admin?.username}
                                    {...register('admin.username', {
                                      required: true,
                                      setValueAs: (value) => value.trim(),
                                      onChange: (e) =>
                                        setValue(
                                          'admin.username',
                                          e.target.value.replace(/\s/g, '_')
                                        )
                                    })}
                                  />

                                  {/* Nombre Completo */}
                                  <Input
                                    label={t('account-page.modal-add-account.label-fullname')}
                                    placeholder={t(
                                      'account-page.modal-add-account.placeholder-fullname'
                                    )}
                                    requiredIndicator="required"
                                    errors={errors.admin?.full_name}
                                    {...register('admin.full_name', {
                                      required: true,
                                      setValueAs: (value) => value.trim()
                                    })}
                                  />

                                  {/* Correo Electrónico */}
                                  <Input
                                    label={t('account-page.modal-add-account.label-email')}
                                    placeholder={t(
                                      'account-page.modal-add-account.placeholder-email'
                                    )}
                                    errors={errors.admin?.email}
                                    requiredIndicator="required"
                                    {...register('admin.email', {
                                      required: true,
                                      setValueAs: (value) => value.trim(),
                                      pattern: {
                                        value: emailRegex,
                                        message: t('account-page.error-email')
                                      }
                                    })}
                                  />

                                  {/* Botones */}
                                  <div className="d-flex justify-content-start gap-5">
                                    <Button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={handleSubmit(handleSaveUser)}
                                    >
                                      <Icon icon="ki-user-tick" size="fs-1" className="ms-2" />
                                      {t('account-page.modal-add-account.save-user')}
                                    </Button>
                                    <Button
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={() => setShowUserForm(false)}
                                    >
                                      {t('general.close')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/*Tercer stepper*/}
                        <div className="flex-column" data-kt-stepper-element="content">
                          <SimpleTitle
                            title={t('account-page.modal-add-account.stepper-3-title1')}
                            subtitle={t('account-page.modal-add-account.stepper-3-subtitle1')}
                          />
                          <div className="alert alert-dismissible bg-light-primary border border-primary d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                            <Icon icon="ki-information" size="fs-2hx" type="primary" />
                            <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                              <h5 className="mb-1 text-primary fw-bolder fs-3 mb-3">
                                {t('general.important')}
                              </h5>
                              <span>{t('account-page.modal-add-account.channels.disclaimer')}</span>
                            </div>
                          </div>
                          <AdvancedCheckbox
                            icon={'ki-whatsapp'}
                            title={t('account-page.modal-add-account.stepper-3-ch1')}
                            subtitle={t('account-page.modal-add-account.stepper-3-ch1-subtitle')}
                            checked={channelConfig.whatsapp}
                            onChange={() => handleCheckboxChange('whatsapp')}
                            value={'1'}
                          />
                          {channelConfig.whatsapp && (
                            <div className="mh-1000px">
                              {whatsappFields.map((field, index) => {
                                const whatsappSelected = watch('channels.whatsapp') || [];

                                const filteredOptions = listData.channelWhatsApp.filter(
                                  (option) =>
                                    !whatsappSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );

                                return (
                                  <div className="row mb-3 align-items-center" key={field.id}>
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.whatsapp.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selected) =>
                                                    onChange(selected ? selected.value : '')
                                                  }
                                                  value={
                                                    listData.channelWhatsApp.find(
                                                      (op) => op.value === value
                                                    ) || null
                                                  }
                                                />
                                                {error && <InvalidFeedback error={error} />}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={errors.channels?.whatsapp?.[index]?.price}
                                            {...register(`channels.whatsapp.${index}.price`, {
                                              required: !!watch(`channels.whatsapp.${index}._id`),
                                              setValueAs: (value) => value.trim(),
                                              pattern: {
                                                value: priceRegex,
                                                message: t('forms.invalid-FeedBack.valid-price')
                                              }
                                            })}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {whatsappFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => whatsappRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const whatsappSelectedOverall = watch('channels.whatsapp') || [];

                                const availableOptions = listData.channelWhatsApp.filter(
                                  (option) =>
                                    !whatsappSelectedOverall.some(
                                      (item) => item._id === option.value
                                    )
                                );

                                if (
                                  availableOptions.length > 0 &&
                                  whatsappFields.length < listData.channelWhatsApp.length
                                ) {
                                  return (
                                    <div className="d-flex justify-content-start align-items-center mb-4">
                                      <Button
                                        type="button"
                                        className="btn btn-sm btn-light-info hover-scale text-nowrap me-4"
                                        onClick={() => whatsappAppend({ _id: '', price: '' })}
                                      >
                                        <Icon icon="ki-plus" size="fs-2" />
                                        {t('account-page.modal-add-account.add-another-account')}
                                      </Button>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <div className="separator separator-dashed text-dark mb-5"></div>
                          <AdvancedCheckbox
                            type="info"
                            icon={'ki-messages'}
                            title={t('account-page.modal-add-account.stepper-3-ch2')}
                            subtitle={t('account-page.modal-add-account.stepper-3-rcs-subtitle')}
                            checked={channelConfig.rcs_conversational}
                            onChange={() => handleCheckboxChange('rcs_conversational')}
                            value={'1'}
                          />
                          {channelConfig.rcs_conversational && (
                            <div className="mh-1000px ">
                              {rcsConversationalFields.map((field, index) => {
                                const rcsConvSelected = watch('channels.rcs_conversational') || [];
                                const filteredOptions = listData.channelRcsConversacional.filter(
                                  (option) =>
                                    !rcsConvSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );
                                return (
                                  <div className="row mb-3 align-items-center" key={field.id}>
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.rcs_conversational.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selected) =>
                                                    onChange(selected ? selected.value : '')
                                                  }
                                                  value={
                                                    listData.channelRcsConversacional.find(
                                                      (op) => op.value === value
                                                    ) || null
                                                  }
                                                />
                                                {error && <InvalidFeedback error={error} />}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={
                                              errors.channels?.rcs_conversational?.[index]?.price
                                            }
                                            {...register(
                                              `channels.rcs_conversational.${index}.price`,
                                              {
                                                required: !!watch(
                                                  `channels.rcs_conversational.${index}._id`
                                                ),
                                                pattern: {
                                                  value: priceRegex,
                                                  message: t('forms.invalid-FeedBack.valid-price')
                                                }
                                              }
                                            )}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {rcsConversationalFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => rcsConversationalRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const rcsConvSelectedOverall =
                                  watch('channels.rcs_conversational') || [];
                                const availableOptions = listData.channelRcsConversacional.filter(
                                  (option) =>
                                    !rcsConvSelectedOverall.some(
                                      (item) => item._id === option.value
                                    )
                                );
                                if (
                                  availableOptions.length > 0 &&
                                  rcsConversationalFields.length <
                                    listData.channelRcsConversacional.length
                                ) {
                                  return (
                                    <div className="d-flex justify-content-start align-items-center mb-4">
                                      <Button
                                        type="button"
                                        className="btn btn-sm btn-light-info hover-scale text-nowrap me-4"
                                        onClick={() =>
                                          rcsConversationalAppend({ _id: '', price: '' })
                                        }
                                      >
                                        <Icon icon="ki-plus" size="fs-2" />
                                        {t('account-page.modal-add-account.add-another-account')}
                                      </Button>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <div className="separator separator-dashed text-dark mb-5"></div>
                          <AdvancedCheckbox
                            type="info"
                            icon={'ki-messages'}
                            title={t('account-page.modal-add-account.stepper-3-ch3')}
                            subtitle={t('account-page.modal-add-account.stepper-3-rcs-subtitle')}
                            checked={channelConfig.rcs_single}
                            onChange={() => handleCheckboxChange('rcs_single')}
                            value={'1'}
                          />
                          {channelConfig.rcs_single && (
                            <div className="mh-1000px ">
                              {rcsSingleFields.map((field, index) => {
                                const rcsSingleSelected = watch('channels.rcs_single') || [];
                                const filteredOptions = listData.channelRcsSingle.filter(
                                  (option) =>
                                    !rcsSingleSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );
                                return (
                                  <div className="row mb-3 align-items-center" key={field.id}>
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.rcs_single.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selected) =>
                                                    onChange(selected ? selected.value : '')
                                                  }
                                                  value={
                                                    listData.channelRcsSingle.find(
                                                      (op) => op.value === value
                                                    ) || null
                                                  }
                                                />
                                                {error && <InvalidFeedback error={error} />}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={errors.channels?.rcs_single?.[index]?.price}
                                            {...register(`channels.rcs_single.${index}.price`, {
                                              required: !!watch(`channels.rcs_single.${index}._id`),
                                              pattern: {
                                                value: priceRegex,
                                                message: t('forms.invalid-FeedBack.valid-price')
                                              }
                                            })}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {rcsSingleFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => rcsSingleRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const rcsSingleSelectedOverall = watch('channels.rcs_single') || [];
                                const availableOptions = listData.channelRcsSingle.filter(
                                  (option) =>
                                    !rcsSingleSelectedOverall.some(
                                      (item) => item._id === option.value
                                    )
                                );
                                if (
                                  availableOptions.length > 0 &&
                                  rcsSingleFields.length < listData.channelRcsSingle.length
                                ) {
                                  return (
                                    <div className="d-flex justify-content-start align-items-center mb-4">
                                      <Button
                                        type="button"
                                        className="btn btn-sm btn-light-info hover-scale text-nowrap me-4"
                                        onClick={() => rcsSingleAppend({ _id: '', price: '' })}
                                      >
                                        <Icon icon="ki-plus" size="fs-2" />
                                        {t('account-page.modal-add-account.add-another-account')}
                                      </Button>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <div className="separator separator-dashed text-dark mb-5"></div>
                          <AdvancedCheckbox
                            type="info"
                            icon={'ki-messages'}
                            title={t('account-page.modal-add-account.stepper-3-ch4')}
                            subtitle={t('account-page.modal-add-account.stepper-3-rcs-subtitle')}
                            checked={channelConfig.rcs_basic}
                            onChange={() => handleCheckboxChange('rcs_basic')}
                            value={'1'}
                          />
                          {channelConfig.rcs_basic && (
                            <div className="mh-1000px ">
                              {rcsBasicFields.map((field, index) => {
                                const rcsBasicSelected = watch('channels.rcs_basic') || [];
                                const filteredOptions = listData.channelRcsBasic.filter(
                                  (option) =>
                                    !rcsBasicSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );
                                return (
                                  <div className="row mb-3 align-items-center" key={field.id}>
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.rcs_basic.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selected) =>
                                                    onChange(selected ? selected.value : '')
                                                  }
                                                  value={
                                                    listData.channelRcsBasic.find(
                                                      (op) => op.value === value
                                                    ) || null
                                                  }
                                                />
                                                {error && <InvalidFeedback error={error} />}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={errors.channels?.rcs_basic?.[index]?.price}
                                            {...register(`channels.rcs_basic.${index}.price`, {
                                              required: !!watch(`channels.rcs_basic.${index}._id`),
                                              pattern: {
                                                value: priceRegex,
                                                message: t('forms.invalid-FeedBack.valid-price')
                                              }
                                            })}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {rcsBasicFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => rcsBasicRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const rcsBasicSelectedOverall = watch('channels.rcs_basic') || [];
                                const availableOptions = listData.channelRcsBasic.filter(
                                  (option) =>
                                    !rcsBasicSelectedOverall.some(
                                      (item) => item._id === option.value
                                    )
                                );
                                if (
                                  availableOptions.length > 0 &&
                                  rcsBasicFields.length < listData.channelRcsBasic.length
                                ) {
                                  return (
                                    <div className="d-flex justify-content-start align-items-center mb-4">
                                      <Button
                                        type="button"
                                        className="btn btn-sm btn-light-info hover-scale text-nowrap me-4"
                                        onClick={() => rcsBasicAppend({ _id: '', price: '' })}
                                      >
                                        <Icon icon="ki-plus" size="fs-2" />
                                        {t('account-page.modal-add-account.add-another-account')}
                                      </Button>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <div className="separator separator-dashed text-dark mb-5"></div>
                          <AdvancedCheckbox
                            type="warning"
                            icon={'ki-messages'}
                            title={t('account-page.modal-add-account.stepper-3-ch5')}
                            subtitle={t('account-page.modal-add-account.stepper-3-ch5-subtitle')}
                            checked={channelConfig.sms}
                            onChange={() => handleCheckboxChange('sms')}
                            value={'1'}
                          />

                          {channelConfig.sms && (
                            <div className="mh-1000px">
                              {smsFields.map((field, index) => {
                                const smsSelected = watch('channels.sms') || [];
                                const filteredOptions = listData.channelSMS.filter(
                                  (option) =>
                                    !smsSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );
                                return (
                                  <div
                                    className="row mb-3 align-items-center z-index-3"
                                    key={field.id}
                                  >
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.sms.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selected) =>
                                                    onChange(selected ? selected.value : '')
                                                  }
                                                  value={
                                                    listData.channelSMS.find(
                                                      (op) => op.value === value
                                                    ) || null
                                                  }
                                                />
                                                {error && <InvalidFeedback error={error} />}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={errors.channels?.sms?.[index]?.price}
                                            {...register(`channels.sms.${index}.price`, {
                                              required: !!watch(`channels.sms.${index}._id`),
                                              setValueAs: (value) => value.trim(),
                                              pattern: {
                                                value: priceRegex,
                                                message: t('forms.invalid-FeedBack.valid-price')
                                              }
                                            })}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {smsFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => smsRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const smsSelectedOverall = watch('channels.sms') || [];
                                const availableOptions = listData.channelSMS.filter(
                                  (option) =>
                                    !smsSelectedOverall.some((item) => item._id === option.value)
                                );
                                if (
                                  availableOptions.length > 0 &&
                                  smsFields.length < listData.channelSMS.length
                                ) {
                                  return (
                                    <div className="d-flex justify-content-start align-items-center mb-4">
                                      <Button
                                        type="button"
                                        className="btn btn-sm btn-light-info hover-scale text-nowrap me-4"
                                        onClick={() => smsAppend({ _id: '', price: '' })}
                                      >
                                        <Icon icon="ki-plus" size="fs-2" />
                                        {t('account-page.modal-add-account.add-another-output')}
                                      </Button>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <div className="separator separator-dashed text-dark mb-5"></div>
                          <AdvancedCheckbox
                            type="primary"
                            icon={'ki-message-text-2'}
                            title={'Email'}
                            subtitle={'Configura las cuentas de email para este canal'}
                            checked={channelConfig.email}
                            onChange={() => handleCheckboxChange('email')}
                            value={'1'}
                          />

                          {channelConfig.email && (
                            <div className="mh-1000px">
                              {emailFields.map((field, index) => {
                                const emailSelected = watch('channels.email') || [];
                                const filteredOptions = listData.channelEmail.filter(
                                  (option) =>
                                    !emailSelected.some(
                                      (item, idx) => item._id === option.value && idx !== index
                                    )
                                );
                                return (
                                  <div className="row mb-3 align-items-center" key={field.id}>
                                    <div className="col-md-10">
                                      <div className="row">
                                        <div className="col-md-9 my-auto">
                                          <label className="form-label fw-bold">
                                            {t(
                                              'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                            )}
                                          </label>
                                          <span className="required"></span>
                                          <Controller
                                            control={control}
                                            name={`channels.email.${index}._id`}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, value },
                                              fieldState: { error }
                                            }) => (
                                              <>
                                                <Select
                                                  options={filteredOptions}
                                                  noOptionsMessage={({ inputValue }) =>
                                                    inputValue
                                                      ? t('general.no-options', { inputValue })
                                                      : t('general.no-options')
                                                  }
                                                  className={
                                                    error
                                                      ? 'form-select-custom is-invalid'
                                                      : 'form-select-custom'
                                                  }
                                                  classNamePrefix="form-select-custom"
                                                  placeholder={t(
                                                    'account-page.modal-add-account.stepper-3-input-accounts-placeholder'
                                                  )}
                                                  onChange={(selectedOption) => {
                                                    onChange(
                                                      selectedOption ? selectedOption.value : ''
                                                    );
                                                  }}
                                                  value={
                                                    listData.channelEmail.find(
                                                      (option) => option.value === value
                                                    ) || null
                                                  }
                                                />
                                                <InvalidFeedback error={error} />
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          <Input
                                            label={t(
                                              'account-page.modal-add-account.stepper-3-input-price'
                                            )}
                                            requiredIndicator="required"
                                            placeholder={t(
                                              'account-page.modal-add-account.stepper-3-input-price-placeholder'
                                            )}
                                            onKeyDown={handlePrice}
                                            errors={errors?.channels?.email?.[index]?.price}
                                            {...register(`channels.email.${index}.price`, {
                                              required: true,
                                              pattern: {
                                                value: priceRegex,
                                                message: t('forms.invalid-FeedBack.valid-price')
                                              },
                                              min: {
                                                value: 0.01,
                                                message: t('forms.invalid-FeedBack.min-price')
                                              }
                                            })}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {emailFields.length > 1 && (
                                      <div className="col-md-2 text-center">
                                        <Button
                                          type="button"
                                          className="btn btn-sm btn-light-danger mt-6"
                                          onClick={() => emailRemove(index)}
                                        >
                                          <Icon icon="ki-trash" size="fs-2" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
              <AddCompanyModal
                show={isAddCompanyModalOpen}
                onRequestClose={() => setIsAddCompanyModalOpen(false)}
                onAdd={handleAddCompany}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default AccountStepper;
